import i18next from 'i18next'
import { importSPKI, jwtVerify } from 'jose'
import User from 'state/models/User'
import publicKey from 'utils/../jwt-ecdsa-p256-pub.pem'
import { store } from 'state/_store'
import { getUser, logout } from 'state/Auth'
import { Alerts } from 'utils/index'

export class JWTCheckError extends Error {
	jwt = null

	constructor(jwt, msg) {
		super(msg)
		this.jwt = jwt
	}
}

export class JWTExpired extends JWTCheckError {
	constructor(jwt) {
		super(jwt, '"exp" claim timestamp check failed')
	}
}

export class JWTMissingClaim extends JWTCheckError {
	claim = null

	constructor(jwt, claim) {
		const msg = `JWT is missing requested claim: ${claim}`
		super(jwt, msg)
		this.claim = claim
	}
}

/**
 * @param stateOrUser {object|User} Either the whole state object, or a pre-selected User instance
 * @param jwt {string} The actual JSON Web Token
 * @param claim {string} A claim you want the result back, if the check is successful
 * @param forceLogout {boolean} If the check fails, forces logout with a "please log in again" error alert.
 * @returns {Promise<true|string>}
 * @throws {JWTMissingClaim} The requested claim wasn't present in the token, other than that, it validated successfully
 * @throws {JWTExpired} The token has passed its timeout
 * @throws {Error} Most probably, an internal issue validating the JWT
 */
export const checkJWT = async (stateOrUser, jwt, { claim = '', forceLogout = false } = {}) => {
	const ALGORITHM = 'ES256'
	const key       = await importSPKI(publicKey, ALGORITHM)
	const user      = stateOrUser instanceof User? stateOrUser : getUser(stateOrUser)

	try {
		//may be called more than one time in race condition scenarios
		console.debug('Decoding JWT', { claim, userId: user.id, /*ALGORITHM, key, publicKey,*/ jwt })
		const { payload } = await jwtVerify(jwt, key, {
			algorithms: [ALGORITHM],
			subject   : `${user.id}`, //jwt.io shows as string, but it isn't? but the PHP (lcobucci) asserts considers it to be?
		})
		if (claim) {
			if (claim in payload) {
				return payload[claim]
			} else { // noinspection ExceptionCaughtLocallyJS
				throw new JWTMissingClaim(jwt, claim)
			}
		} else {
			return true
		}
	} catch (e) {
		if (e instanceof JWTCheckError) {
			console.warn(e)
			throw e //our own exceptions, just throw it up
		} else if (e.name == 'JWTExpired') {
			console.warn(e)
			throw new JWTExpired(jwt)
		}

		console.error(e)

		if (forceLogout) { //forcing logout since this is probably cause by some type of tampering?
			Alerts.danger(i18next.t('Sorry, we had an issue validating your permissions. Please, log in again.')+' '+i18next.t('CONTACT_FURTHER_HELP')) //TODO generalize this error message, or allow for placeholder on what's being validated
			store.dispatch(logout)
		}
		return false
	}
}
